import React, { createContext, useContext, useState, useEffect } from 'react';
import { Course, Settings } from '../types/course';
import { initialSettings } from '../data/initialData';
import { fetchCourses, saveCourse, updateCourse, deleteCourse } from '../lib/firebase/courses';
import { fetchSettings, saveSettings } from '../lib/firebase/settings';
import { localStore } from '../lib/storage';

interface DataContextType {
  courses: Course[];
  settings: Settings;
  loading: boolean;
  error: string | null;
  addCourse: (course: Partial<Course>) => Promise<void>;
  updateCourse: (course: Course) => Promise<void>;
  deleteCourse: (id: string) => Promise<void>;
  updateSettings: (settings: Settings) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: React.ReactNode }) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [settings, setSettings] = useState<Settings>(initialSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les données initiales
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [loadedCourses, loadedSettings] = await Promise.all([
        fetchCourses(),
        fetchSettings()
      ]);
      setCourses(loadedCourses);
      if (loadedSettings) {
        setSettings(loadedSettings);
      }
      setError(null);
    } catch (error) {
      console.error('Erreur de chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const addCourse = async (courseData: Partial<Course>) => {
    try {
      setError(null);
      const maxOrder = Math.max(...courses.map(c => c.order || 0), 0);
      const newCourse = await saveCourse({
        ...courseData,
        order: maxOrder + 1
      });
      setCourses(prev => [...prev, newCourse]);
    } catch (error) {
      console.error('Erreur d\'ajout:', error);
      setError('Erreur lors de l\'ajout du cours');
      throw error;
    }
  };

  const value = {
    courses,
    settings,
    loading,
    error,
    addCourse,
    updateCourse: async (course: Course) => {
      try {
        setError(null);
        await updateCourse(course.id, course);
        setCourses(prev => prev.map(c => c.id === course.id ? course : c));
      } catch (error) {
        setError('Erreur lors de la mise à jour du cours');
        throw error;
      }
    },
    deleteCourse: async (id: string) => {
      try {
        setError(null);
        await deleteCourse(id);
        setCourses(prev => prev.filter(c => c.id !== id));
      } catch (error) {
        setError('Erreur lors de la suppression du cours');
        throw error;
      }
    },
    updateSettings: async (newSettings: Settings) => {
      try {
        setError(null);
        await saveSettings(newSettings);
        setSettings(newSettings);
      } catch (error) {
        setError('Erreur lors de la mise à jour des paramètres');
        throw error;
      }
    }
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}